/**
 * Returns the attachment type label for chapter/episode based on content type.
 * @param {string} contentType
 * @returns {string|undefined}
 */
export const getChapterOrEpisodeType = (contentType) => {
  const types = {
    Dramaturgia: "Capítulo/Episódio",
    Variedades: "Episódio",
  };
  return types[contentType];
};

/**
 * Flattens raw attachment data into a single list, including older versions.
 * @param {Array} data
 * @returns {Array}
 */
export const flatMapFilesAndVersions = (data) =>
  data.reduce((acc, entry) => {
    if (!entry) return acc;

    acc.push({ ...entry.attachment, activitiesNumber: entry.activitiesNumber });

    if (entry.olderVersions) {
      for (const old of entry.olderVersions) {
        acc.push({ ...old, activitiesNumber: entry.activitiesNumber });
      }
    }

    return acc;
  }, []);

/**
 * Splits a flat list of attachment files into parent + versions groups.
 * @param {Array} files
 * @returns {Array<{ parent: object, version: Array }>}
 */
export const toSeparateVersion = (files) => {
  const obj = [];
  files.forEach((file) => {
    if (!file?.length) return;
    obj.push({ parent: file[0], version: file.filter((e) => e !== file[0]) });
  });
  return obj;
};

/**
 * Compiles a flat attachment list into grouped parent + versions entries.
 * Chapter/episode groups are listed first; the remaining attachment groups are
 * sorted by attachment type name in ascending order.
 * Shared by NewActivity (selection/modal flow) and ContentItem (visible list).
 * @param {object} attachmentContext - AttachmentsContext value
 * @param {Array} data - flat attachment list (see flatMapFilesAndVersions)
 * @param {string} chapterEpisodeType - "Capítulo/Episódio" | "Episódio"
 * @param {string} type - project/label type forwarded to compiledChapter
 * @returns {Array<{ parent: object, version: Array }>}
 */
export const compileAttachmentsForPresentation = ({
  attachmentContext,
  data,
  chapterEpisodeType,
  type,
  groupSupplementaryMaterial,
}) => {
  const allGroup = attachmentContext.compileAll(
    data,
    chapterEpisodeType,
    groupSupplementaryMaterial
  );
  const filteredAllGroup = Object.fromEntries(
    Object.entries(allGroup).map(([key, files]) => [
      key,
      files.filter((file) => file.fileId && file.fileName),
    ])
  );
  const chapterCompiled = attachmentContext.compiledChapter(
    data,
    type,
    chapterEpisodeType
  );
  const addendumCompiled = attachmentContext.compileAddendum(data);
  const otherGroups = Object.values({
    ...filteredAllGroup,
    ...addendumCompiled,
  }).sort((a, b) => {
    const typeA = a?.[0]?.attachmentType?.name ?? a?.[0]?.type?.name ?? "";
    const typeB = b?.[0]?.attachmentType?.name ?? b?.[0]?.type?.name ?? "";

    return typeA.localeCompare(typeB);
  });

  return toSeparateVersion(
    Object.values({
      ...chapterCompiled,
    }).concat(otherGroups)
  );
};
